import React, { useState } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { TableVirtuoso } from 'react-virtuoso';
import { useTicks } from '../api';

interface Props {
  segment: string;
  date: string;
  symbol: string;
}

export default function TickTable({ segment, date, symbol }: Props) {
  const [offset] = useState(0);
  const { data, isLoading } = useTicks(segment, date, symbol, offset, 10000);

  if (isLoading) return <LinearProgress sx={{ borderRadius: 1 }} />;
  if (!data?.rows.length) {
    return <Typography color="text.secondary">No tick data.</Typography>;
  }

  return (
    <Box flex={1} minHeight={300}>
      <Typography variant="caption" color="text.secondary" mb={0.5} display="block">
        Showing {data.rows.length.toLocaleString()} of {data.total.toLocaleString()} ticks
      </Typography>

      <TableVirtuoso
        style={{ height: 'calc(100% - 24px)', borderRadius: 8, border: '1px solid rgba(148,163,184,0.2)' }}
        data={data.rows}
        fixedHeaderContent={() => (
          <tr style={{ background: 'rgba(30,41,59,0.8)' }}>
            <th style={headerStyle}>Time</th>
            <th style={{ ...headerStyle, textAlign: 'right' }}>LTP</th>
            <th style={{ ...headerStyle, textAlign: 'right' }}>Qty</th>
            <th style={{ ...headerStyle, textAlign: 'right' }}>OI</th>
          </tr>
        )}
        itemContent={(_, row) => (
          <>
            <td style={cellStyle}>{formatTime(row.ts)}</td>
            <td style={{ ...cellStyle, textAlign: 'right', fontFamily: 'monospace' }}>
              {row.ltp.toFixed(2)}
            </td>
            <td style={{ ...cellStyle, textAlign: 'right', fontFamily: 'monospace' }}>
              {row.ltq.toLocaleString()}
            </td>
            <td style={{ ...cellStyle, textAlign: 'right', fontFamily: 'monospace' }}>
              {row.oi.toLocaleString()}
            </td>
          </>
        )}
      />
    </Box>
  );
}

function formatTime(ts: string): string {
  // Extract HH:MM:SS from ISO timestamp.
  const match = ts.match(/T?(\d{2}:\d{2}:\d{2})/);
  return match ? match[1] : ts.slice(11, 19);
}

const headerStyle: React.CSSProperties = {
  padding: '6px 10px',
  fontSize: 11,
  fontWeight: 700,
  textAlign: 'left',
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  borderBottom: '1px solid rgba(148,163,184,0.2)',
};

const cellStyle: React.CSSProperties = {
  padding: '3px 10px',
  fontSize: 12,
  borderBottom: '1px solid rgba(148,163,184,0.08)',
};
